import { getCurrentToken } from '../utils/auth';
import { v4 as uuid } from "uuid";
import { ArtType } from '../pages/art/Art';

type AIRequest = {
    text: string;
    image: string;
    image_size: number;
    model?: string;
    image_creativity?: number;
}

interface generateImageProps {
    req: AIRequest;
    config: any;
    artType?: ArtType;
}

type RemoveBGRequest = {
    image: string;
    model?: string;
}

interface removeBGProps {
    req: RemoveBGRequest;
    config: any;
}

type AIResult = { 
    url: string;
    width?: number;
    height?: number;
}

export const generateAIImage = async ({ req, config, artType='2D' }: generateImageProps): Promise<AIResult> => {
    const startTime = Date.now();
    return new Promise(async (resolve, reject) => {
        // console.log('Sending request to backend:', {
        //     hasText: !!req.text,
        //     hasImage: !!req.image,
        //     imageSize: req.image_size
        // });
        
        const id = uuid();
        let timer: ReturnType<typeof setTimeout>;
        let isRequestCancelled = false;

        const cleanup = () => {
            if (timer) {
                clearTimeout(timer);
            }
            isRequestCancelled = true;
        };

        try {
            // Set up timeout
            timer = setTimeout(() => {
                console.error('Image generation timed out after', config.AUTO_GENERATE_IMAGE_TIMEOUT, 'ms');
                cleanup();
                reject(new Error('Image generation timed out'));
            }, config.AUTO_GENERATE_IMAGE_TIMEOUT);

            // Get auth token for Supabase
            const authToken = await getCurrentToken();

            // Check if image is blank/empty and use generate-image-2 endpoint instead
            if (!req.image) {
                // console.log('Image is blank, using generate-image-2 endpoint');
                
                // Create a specific prompt for game art asset
                const gameArtPrompt = `Create the following ${artType} game art asset: ${req.text}` + ' (1:1 square aspect ratio)';
                
                // Call the generate-image-2 endpoint
                const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-image-2`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        image: '', // Empty image
                        prompt: gameArtPrompt
                    })
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (isRequestCancelled) return;
                
                if (result.image) {
                    cleanup();
                    const endTime = Date.now();
                    const duration = endTime - startTime;
                    // console.log(`Image generation completed in ${duration}ms`);
                    // console.log('Received image 2 result: ', result);
                    resolve({ url: result.image });
                    return;
                } else {
                    console.warn('Received empty result from generate-image-2:', result);
                    cleanup();
                    reject(new Error('Empty result received from AI service'));
                    return;
                }
            }

            const append2d = ' beautiful award-winning professional game art';
            const prompt2d = req.text + append2d;
            const negativePrompt2d = 'amateur, low quality, text, signature';

            const append3d = 'beautiful artistic high-quality rendering of a 3D model for a video game';
            const prompt3d = append3d + `\n` + req.text;
            const negativePrompt3d = 'amateur, low quality, background, shadows';

            const body = JSON.stringify({
                operation: 'generate',
                prompt: artType === '2D' ? prompt2d : prompt3d,
                negative_prompt: artType === '2D' ? negativePrompt2d : negativePrompt3d,
                image_url: req.image,
                image_size: {
                    width: config.IMAGE_SIZE,
                    height: config.IMAGE_SIZE,
                },
                num_images: 1,
                sync_mode: true,
                expand_prompt: true, // default false
                num_inference_steps: 4, // default 2
                guidance_scale: 1.4, // default 1, max 2: prompt adherence
                strength: req.image_creativity, // default 0.95: image creativity
                seed: 42,
                // loras: [{
                //     path: 'https://huggingface.co/artificialguybr/PixelArtRedmond/resolve/main/PixelArtRedmond-Lite64.safetensors',
                //     scale: 1,
                // }],
                // guidance_scale: 7.5, // default 7.5, max 20: prompt adherence (for loras)
                // strength: 0.95,
            });

            // console.log('Sending image generation request:', {
            //     id,
            //     body
            // });

            // Call the Supabase Edge Function as a proxy to fal.ai
            const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-image`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: body
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();

            if (isRequestCancelled) return;

            if (result.images && result.images[0]) {
                const imageData = result.images[0];
                
                // Check if the image already has a URL we can use directly
                if (imageData.url) {
                    cleanup();
                    const endTime = Date.now();
                    const duration = endTime - startTime;
                    // console.log(`Image generation completed in ${duration}ms`);
                    resolve({ url: imageData.url });
                    return;
                }
                
                // Handle binary content if available
                if (imageData.content) {
                    let base64String;
                    
                    // Handle different content formats
                    if (typeof imageData.content === 'string') {
                        // If content is already a base64 string
                        base64String = imageData.content;
                    } else {
                        // If content is a Uint8Array or array
                        base64String = btoa(
                            String.fromCharCode.apply(null, Array.from(imageData.content))
                        );
                    }
                    
                    const imageUrl = `data:${imageData.content_type || 'image/png'};base64,${base64String}`;
                    cleanup();
                    const endTime = Date.now();
                    const duration = endTime - startTime;
                    // console.log(`Image generation completed in ${duration}ms`);
                    resolve({ url: imageUrl });
                } else {
                    console.warn('Image data missing content or URL:', imageData);
                    cleanup();
                    reject(new Error('Image data missing content or URL'));
                }
            } else {
                console.warn('Received empty result:', result);
                cleanup();
                reject(new Error('Empty result received from AI service'));
            }
        } catch (error) {
            console.error('Error during image generation:', error);
            cleanup();
            reject(error);
        }
    });
};

export const removeBackground = async ({ req, config }: removeBGProps): Promise<AIResult> => {
    const startTime = Date.now();
    return new Promise(async (resolve, reject) => {
        // console.log('Sending request to backend for background removal:', {
        //     hasImage: !!req.image
        // });

        // Create a timeout for the request
        const timeoutId = setTimeout(() => {
            console.error('Background removal timed out after', config.AUTO_GENERATE_IMAGE_TIMEOUT, 'ms');
            reject(new Error('Background removal timed out'));
        }, config.AUTO_GENERATE_IMAGE_TIMEOUT);

        const authToken = await getCurrentToken();

        // Call the Supabase Edge Function
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/remove-background`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
                image: req.image,
            })
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            clearTimeout(timeoutId);
            // console.log('Received background image result: ', data);
            const endTime = Date.now();
            const duration = endTime - startTime;
            // console.log(`Background removal completed in ${duration}ms`);
            resolve({ url: data.url });
        })
        .catch(error => {
            clearTimeout(timeoutId);
            console.error('Error during background removal:', error);
            reject(error);
        });
    });
};
